// Left body column for Today view (desktop lg+).
// Renders: Việc hôm nay (non-MIT) · Lịch hôm nay · Mục tiêu đang theo (A6) · Dự án đang chạy.
// A6: "Mục tiêu đang theo" tile — top 3 active goals with progress bars.
// B9: Goal pill on project rows linking to goal via onGoto("goals").
import { ArrowRight, ChevronRight, FolderKanban, Target } from "lucide-react";
import type { LifeOsApi } from "../lib/life-os-contract";
import type { Task } from "../types";
import { deriveProjectProgress, deriveProjectTaskCounts, goalDisplayPercent } from "./bento-derive";
import { PRIO, ProgressBar, Tile, TileHead, fullDate } from "./bento-ui";

const link = "text-[12px] font-bold text-brand-600";

interface Props {
  api: LifeOsApi;
  /** Due-today + in-progress tasks with MIT duplicates already removed. */
  nonMitTasks: Task[];
  todayEvents: { id: string; time: string; title: string; color?: string }[];
  activeProjects: { id: string; name: string; nextAction: string }[];
  onGoto: (v: string) => void;
}

export function TodayLeftColumn({ api, nonMitTasks, todayEvents, activeProjects, onGoto }: Props) {
  return (
    <div className="col-span-2 flex flex-col gap-3.5">
      {/* Việc hôm nay — MIT tasks excluded (those are already shown in HERO) */}
      <Tile>
        <TileHead
          title="Việc hôm nay"
          action={<button className={link} onClick={() => onGoto("tasks")}>Tất cả</button>}
        />
        <div className="space-y-2.5">
          {nonMitTasks.map((t) => (
            <div key={t.id} className="group flex items-center gap-3 rounded-2xl bg-ink-200/30 px-3 py-2.5">
              <button
                onClick={() => api.actions.updateTask(t.id, { status: t.status === "done" ? "todo" : "done" })}
                className="grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 border-ink-300"
                aria-label="Hoàn thành"
              />
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: PRIO[t.priority] }} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-semibold">{t.title}</p>
                <p className="truncate text-[11px] text-ink-500">
                  {[fullDate(t.dueDate), `${t.estimateMinutes}m`].join(" · ")}
                </p>
              </div>
            </div>
          ))}
          {nonMitTasks.length === 0 && (
            <p className="text-[13px] text-ink-500">Không có việc nào khác đến hạn hôm nay 🎉</p>
          )}
        </div>
      </Tile>

      {/* Lịch hôm nay */}
      <Tile>
        <TileHead
          title="Lịch hôm nay"
          action={<button className={link} onClick={() => onGoto("calendar")}>Lịch</button>}
        />
        <div className="space-y-2.5">
          {todayEvents.map((e) => (
            <div key={e.id} className="flex items-center gap-3 rounded-2xl bg-ink-200/30 px-3 py-2.5">
              <span
                className="rounded-lg px-2 py-1 text-[12px] font-bold"
                style={{ background: `${e.color ?? "#7c3aed"}1f`, color: e.color ?? "#7c3aed" }}
              >
                {e.time}
              </span>
              <p className="flex-1 text-[14px] font-semibold">{e.title}</p>
              <ChevronRight className="h-4 w-4 text-ink-300" />
            </div>
          ))}
          {todayEvents.length === 0 && <p className="text-[13px] text-ink-500">Hôm nay không có lịch.</p>}
        </div>
      </Tile>

      {/* A6: Mục tiêu đang theo — top 3 active goals with progress bars */}
      {(() => {
        const activeGoals = api.state.goals.filter((g) => g.status === "active").slice(0, 3);
        if (activeGoals.length === 0) return null;
        return (
          <Tile>
            <TileHead
              title="Mục tiêu đang theo"
              action={<button className={link} onClick={() => onGoto("goals")}>Tất cả</button>}
            />
            <div className="space-y-3">
              {activeGoals.map((g) => {
                const pct = goalDisplayPercent(api.state, g);
                return (
                  <button
                    key={g.id}
                    onClick={() => onGoto("goals")}
                    className="flex w-full items-start gap-3 rounded-2xl bg-ink-100/40 px-3 py-2.5 text-left hover:bg-ink-100/70 transition"
                    aria-label={`Mở mục tiêu: ${g.title}`}
                  >
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-brand-100 text-brand-600">
                      <Target className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <p className="truncate text-[13px] font-semibold">{g.title}</p>
                        <span className="ml-2 shrink-0 text-[12px] font-bold text-brand-600">{pct}%</span>
                      </div>
                      <div className="mt-1.5"><ProgressBar percent={pct} /></div>
                    </div>
                  </button>
                );
              })}
            </div>
          </Tile>
        );
      })()}

      {/* Dự án đang chạy */}
      <Tile>
        <TileHead
          title="Dự án đang chạy"
          action={<button className={link} onClick={() => onGoto("projects")}>Tất cả</button>}
        />
        <div className="space-y-3.5">
          {activeProjects.map((p) => {
            const displayPct = deriveProjectProgress(api.state, p.id);
            const counts = deriveProjectTaskCounts(api.state, p.id);
            // B9: find linked goal title for goal pill
            const fullProject = api.state.projects.find((proj) => proj.id === p.id);
            const linkedGoal = fullProject?.goalId
              ? api.state.goals.find((g) => g.id === fullProject.goalId)
              : undefined;
            return (
              <div key={p.id} className="flex items-start gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#fef3c7] text-amber-600 dark:bg-amber-500/15 dark:text-amber-300">
                  <FolderKanban className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <p className="truncate text-[14px] font-semibold">{p.name}</p>
                    <span className="ml-2 shrink-0 text-[12px] font-bold text-brand-600">
                      {counts.total > 0 && (
                        <span className="mr-1 font-normal text-ink-400">{counts.done}/{counts.total} việc</span>
                      )}
                      {displayPct}%
                    </span>
                  </div>
                  {/* B9: goal pill */}
                  {linkedGoal && (
                    <button
                      onClick={() => onGoto("goals")}
                      className="mt-1 inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-semibold text-brand-700 hover:bg-brand-100 transition"
                      aria-label={`Mục tiêu: ${linkedGoal.title}`}
                    >
                      <Target className="h-3 w-3 shrink-0" />
                      {linkedGoal.title}
                    </button>
                  )}
                  <div className="mt-1"><ProgressBar percent={displayPct} /></div>
                  <p className="mt-1.5 flex items-center gap-1 text-[12px] text-ink-500">
                    <ArrowRight className="h-3 w-3 text-brand-500" />
                    <span className="font-semibold text-ink-700">Tiếp theo:</span> {p.nextAction}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </Tile>
    </div>
  );
}
